import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useZScroll } from "../hooks/useZScroll";
import SmokeParticles from "./SmokeParticles";
import { SankeyBreakdown } from "./DataFlowVisualization";
import "./DeepDiveLanding.css";

export default function DeepDiveLanding() {
    const scrollZ = useZScroll();
    const [menuOpen, setMenuOpen] = useState(false);
    const [isConstructionOpen, setIsConstructionOpen] = useState(false);
    const isAboutFeaturesEnabled = false;
    const isFeaturesGraphEnabled = false;
    const linksContainerRef = useRef(null);
    const linkRefs = {
        about: useRef(null),
        features: useRef(null),
        contact: useRef(null)
    };
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0, opacity: 0 });

    // Splash screen state
    const [isSplashVisible, setIsSplashVisible] = useState(true);
    const [isSplashComplete, setIsSplashComplete] = useState(false);
    const [isTaglineSplashVisible, setIsTaglineSplashVisible] = useState(true);
    const [isTaglineSplashComplete, setIsTaglineSplashComplete] = useState(false);
    const [startTaglineAnimation, setStartTaglineAnimation] = useState(false);
    const skyTaglineRef = useRef(null);

    // Splash Timing Logic
    useEffect(() => {
        const barDelayMs = 300;
        const barDurationMs = 1500;
        const completionMs = barDelayMs + barDurationMs;
        const slideMs = 700;

        // Logo Splash Slide Up
        const t1 = setTimeout(() => setIsSplashComplete(true), completionMs);
        const t2 = setTimeout(() => setIsSplashVisible(false), completionMs + slideMs);

        // Tagline Animation Start
        const animationStartDelay = 500;
        const t3 = setTimeout(() => setStartTaglineAnimation(true), completionMs + slideMs + animationStartDelay);

        // Tagline Splash Slide Up
        const taglineAnimationTime = 1200;
        const taglineDisplayTime = 1200;
        const taglineStartSlideTime = completionMs + slideMs + animationStartDelay + taglineAnimationTime + taglineDisplayTime;
        const t4 = setTimeout(() => setIsTaglineSplashComplete(true), taglineStartSlideTime);
        const t5 = setTimeout(() => setIsTaglineSplashVisible(false), taglineStartSlideTime + 700);

        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
    }, []);

    // Tagline Text Sizing Logic (Robust Responsive Version)
    useEffect(() => {
        if (!isTaglineSplashVisible) return;

        const el = skyTaglineRef.current;
        if (!el) return;

        let frame = 0;
        let isCancelled = false;

        const fitToViewport = () => {
            if (isCancelled) return;

            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const element = skyTaglineRef.current;
                if (!element) return;

                const isMobile = window.innerWidth <= 768;
                const targetWidthPercent = isMobile ? 0.8 : 0.9;
                const targetWidth = Math.max(window.innerWidth * targetWidthPercent, 1);

                let low = 12, high = 1000, best = 12;
                while (high - low > 0.5) {
                    const mid = (low + high) / 2;
                    element.style.fontSize = `${mid}px`;
                    if (element.scrollWidth > targetWidth) {
                        high = mid;
                    } else {
                        best = mid;
                        low = mid;
                    }
                }
                element.style.fontSize = `${best}px`;
            });
        };

        fitToViewport();
        window.addEventListener('resize', fitToViewport);

        if (document.fonts?.ready) {
            document.fonts.ready.then(() => {
                if (!isCancelled) fitToViewport();
            });
        }

        return () => {
            isCancelled = true;
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', fitToViewport);
        };
    }, [isTaglineSplashVisible]);

    // CSS Var wrapper style
    const blueprintScrollShift = Math.max(0, scrollZ - 2600);
    const tunnelStyle = {
        '--scroll-z': scrollZ,
        '--blueprint-scroll-shift': `${blueprintScrollShift}px`,
    };

    // Determine current section for background transition
    let sectionClass = 'view-home';
    let activeSection = 'home';
    if (scrollZ < 1000) {
        sectionClass = 'view-home';
        activeSection = 'home';
    } else if (scrollZ >= 1000 && scrollZ < 2200) {
        sectionClass = 'view-about';
        activeSection = 'about';
    } else if (scrollZ >= 2200 && scrollZ < 3000) {
        sectionClass = 'view-features';
        activeSection = 'features';
    } else {
        sectionClass = 'view-contact';
        activeSection = 'contact';
    }

    useEffect(() => {
        if (!['about', 'features', 'contact'].includes(activeSection)) {
            setIndicatorStyle((prev) => ({ ...prev, opacity: 0 }));
            return;
        }
        const linkEl = linkRefs[activeSection]?.current;
        const containerEl = linksContainerRef.current;
        if (linkEl && containerEl) {
            const linkRect = linkEl.getBoundingClientRect();
            const containerRect = containerEl.getBoundingClientRect();
            setIndicatorStyle({
                width: linkRect.width,
                left: linkRect.left - containerRect.left,
                opacity: 1
            });
        }
    }, [activeSection]);

    useEffect(() => {
        if (isAboutFeaturesEnabled) return;

        const preventScroll = (event) => {
            event.preventDefault();
        };
        const preventScrollKeys = (event) => {
            const blockedKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
            if (blockedKeys.includes(event.key)) {
                event.preventDefault();
            }
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventScroll, { passive: false });
        window.addEventListener('keydown', preventScrollKeys);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('wheel', preventScroll);
            window.removeEventListener('touchmove', preventScroll);
            window.removeEventListener('keydown', preventScrollKeys);
        };
    }, [isAboutFeaturesEnabled]);

    const scrollToSection = (e, scrollTarget, hash) => {
        e.preventDefault();
        window.history.pushState(null, '', hash);
        window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth'
        });
        setMenuOpen(false);
    };

    const handleConstructionClick = (e) => {
        e.preventDefault();
        setIsConstructionOpen(true);
        setMenuOpen(false);
    };

    // Update URL hash on scroll (Silent Replace)
    useEffect(() => {
        let hash = '';
        switch (sectionClass) {
            case 'view-home': hash = '#'; break;
            case 'view-about': hash = '#about'; break;
            case 'view-features': hash = '#features'; break;
            case 'view-contact': hash = '#contact'; break;
            default: hash = '#';
        }

        // Only replace if changed prevents spam but replaceState is cheap
        if (window.location.hash !== hash) {
            // Check if we are "near" the target to avoid premature swapping? 
            // Actually sectionClass is driven by scrollZ, which is the exact logic we want.
            if (hash === '#') {
                // Remove hash cleanly
                window.history.replaceState(null, '', window.location.pathname);
            } else {
                window.history.replaceState(null, '', hash);
            }
        }
    }, [sectionClass]);

    return (
        <>
            {/* Splash Screens */}
            {isSplashVisible && (
                <div className={`splash-screen${isSplashComplete ? ' is-complete' : ''}`}>
                    <div className="splash-content">
                        <div className="splash-visual">
                            <div className="splash-logo-wrap">
                                <div className="splash-ring">
                                    <svg className="splash-ring__svg" viewBox="0 0 120 120">
                                        <circle className="splash-ring__track" cx="60" cy="60" r="50"></circle>
                                        <circle className="splash-ring__progress" cx="60" cy="60" r="50"></circle>
                                    </svg>
                                </div>
                                <img src="/assets/Logo.svg" alt="EmbodiX" className="splash-logo" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isTaglineSplashVisible && (
                <div className={`tagline-splash-screen${isTaglineSplashComplete ? ' is-complete' : ''}${startTaglineAnimation ? ' animate-words' : ''}`}>
                    <div className="tagline-splash-content">
                        <div className="tagline-splash-text" ref={skyTaglineRef}>
                            <span className="word">Understand</span>
                            <span className="word">the</span>
                            <span className="word">carbon</span>
                            <span className="word">impacts</span>
                            <span className="word">like</span>
                            <span className="word">never</span>
                            <span className="word">before !</span>
                        </div>
                    </div>
                </div>
            )}

            <div className={`deep-dive-container ${sectionClass}`} style={tunnelStyle}>

                {/* Fixed HUD Navigation (Always on top) */}
                <nav className={`hud-nav ${menuOpen ? 'open' : ''} ${scrollZ > 900 ? 'scrolled' : ''}`}>
                    <div className="hud-logo" onClick={(e) => scrollToSection(e, 0, '#')}>
                        EMBODIX<span className="hud-logo-tm">&trade;</span>
                    </div>
                    <div className="hud-links" ref={linksContainerRef}>
                        <span className="hud-active-indicator" style={indicatorStyle}></span>
                        <a
                            ref={linkRefs.about}
                            className={activeSection === 'about' ? 'is-active' : ''}
                            href="#about"
                            onClick={handleConstructionClick}
                        >
                            About
                        </a>
                        <a
                            ref={linkRefs.features}
                            className={activeSection === 'features' ? 'is-active' : ''}
                            href="#features"
                            onClick={handleConstructionClick}
                        >
                            Features
                        </a>
                        <a
                            ref={linkRefs.contact}
                            className={activeSection === 'contact' ? 'is-active' : ''}
                            href="#contact"
                            onClick={(e) => scrollToSection(e, 3500, '#contact')}
                        >
                            Contact
                        </a>
                    </div>
                    <Link to="/start" className="hud-cta">Try it</Link>
                    <button className="hud-toggle" onClick={() => setMenuOpen(!menuOpen)}>
                        <span></span><span></span>
                    </button>
                </nav>

                {/* FIXED VIEWPORT - The "Camera" */}
                <div className="tunnel-viewport">

                    {/* SECTION 1: THE PORTAL (Hero Zoom) */}
                    <div className="tunnel-stage stage-portal">
                        <SmokeParticles />
                        <div className="portal-ring ring-1"></div>
                        {/* Ring 2 Track (Dashed Circle) */}
                        <svg className="portal-ring ring-2 track-layer" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="49.5" className="ring-track" />
                        </svg>

                        {/* Ring 2 Text (Rotating Keywords) */}
                        <svg className="portal-ring ring-2 text-layer" viewBox="0 0 100 100">
                            <defs>
                                <path id="ringPath" d="M 50, 50 m -49.5, 0 a 49.5,49.5 0 1,1 99,0 a 49.5,49.5 0 1,1 -99,0" />
                            </defs>
                            <text className="ring-text" dy="-0.8">
                                <textPath href="#ringPath" startOffset="0%" spacing="auto">
                                    •   NET ZERO   •   MASSING   •   CARBON IMPACT   •   OPTIMIZATION   •   CIRCULARITY
                                    •   INNOVATION   •   RENEWABLES   •   RENOVATION   •   TARGET   •   MATERIALITY
                                    •   EARLY DESIGN   •   SUSTAINABILITY   •   REUSE   •   NATURE   •   LIFE CYCLE ASSESSMENT
                                    •   BUILDING COMPONENT   •   BENCHMARKING   •   SPATIAL   •   MODULARITY
                                </textPath>
                            </text>
                        </svg>
                        <div className="portal-ring ring-3"></div>
                        <h1 className="portal-title">EMBODIX</h1>
                        <div className="scroll-prompt">
                            <span>DIVE IN</span>
                            <div className="line"></div>
                        </div>
                    </div>

                    {/* SECTION 2: THE DEBRIS FIELD (Problem) */}
                    {isAboutFeaturesEnabled && (
                        <div className="tunnel-stage stage-debris" id="about">
                            <div className="debris-text">
                                <h2>The Invisible 80%</h2>
                                <p>Embodied Carbon is the dark matter of construction.</p>
                            </div>
                            {/* Floating Data Shards */}
                            <div className="shard shard-1">CO2</div>
                            <div className="shard shard-2">TRANSPORT</div>
                            <div className="shard shard-3">STEEL</div>
                            <div className="shard shard-4">CONCRETE</div>
                        </div>
                    )}

                    {/* SECTION 3: THE BLUEPRINT (Solution) */}
                    {isAboutFeaturesEnabled && (
                        <div className="tunnel-stage stage-blueprint" id="features">
                            {isFeaturesGraphEnabled && (
                                <SankeyBreakdown isVisible={scrollZ >= 1800 && scrollZ < 3200} />
                            )}
                        </div>
                    )}

                </div>

                {/* SCROLL SPACER */}
                <div className="scroll-spacer"></div>

                {/* FOOTER */}
                <footer className="hologram-footer" id="contact">
                    <div className="horizon-line"></div>
                    <div className="footer-content">
                        <h2>Curious to test it or suggest?</h2>

                        <Link to="/start" className="enter-embodix-btn">
                            Enter EmbodiX
                        </Link>

                        <div className="contact-base">
                            <div className="icon-buttons-row">
                                <a
                                    href="https://www.linkedin.com/in/praveenlk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="icon-btn"
                                    aria-label="Open LinkedIn profile"
                                    title="LinkedIn"
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true" role="img">
                                        <path d="M4.983 3.5C4.983 4.88 3.88 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.483 1.12 2.483 2.5zM.24 8.5h4.52V23H.24V8.5zM8.339 8.5h4.33 v1.98h.06c.6-1.14 2.07-2.34 4.26-2.34 4.56 0 5.4 3 5.4 6.9V23h-4.52v-6.3c0-1.5-.03-3.42-2.08-3.42-2.08 0-2.4 1.62-2.4 3.3V23H8.339V8.5z" />
                                    </svg>
                                </a>
                                <a
                                    href="mailto:praveenlk.2805@gmail.com"
                                    className="icon-btn"
                                    aria-label="Send email"
                                    title="Email"
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 4-8 5-8-5V6l8 5 8-5z" />
                                    </svg>
                                </a>
                            </div>
                            <p className="copyright-text">Copyright © 2025 EmbodiX&trade;. All rights reserved.</p>
                        </div>
                    </div>
                </footer>

            </div>

            {isConstructionOpen && (
                <div className="construction-modal" role="dialog" aria-modal="true" aria-labelledby="construction-title">
                    <button
                        className="construction-backdrop"
                        aria-label="Close"
                        onClick={() => setIsConstructionOpen(false)}
                    ></button>
                    <div className="construction-dialog" role="document">
                        <h3 id="construction-title">About and Features are under construction</h3>
                        <p>Navigate to the 'Contact' page to try the tool, or start right away.</p>
                        <div className="construction-actions">
                            <button
                                className="construction-close"
                                onClick={(event) => {
                                    setIsConstructionOpen(false);
                                    scrollToSection(event, 3500, '#contact');
                                }}
                                type="button"
                            >
                                Go to Contact
                            </button>
                            <Link to="/start" className="construction-cta" onClick={() => setIsConstructionOpen(false)}>
                                Try it
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
